import React, { useEffect, useRef, useState } from 'react'
import { reactular } from '@vendhq/reactular'
import {
  Button,
  FieldLabel,
  GridColumn,
  GridRow,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  CurrencyInput,
  LoaderSpinner,
  NumberInputValidators,
} from '@lightspeed/design-system-react'
import { vn } from 'vend-number'
import { trackUserEvent } from '@vend/utilities'
import { useT } from '@transifex/react'
import ReactDOM from 'react-dom'
import { $rootScope } from '../../../ng-compat/imports'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  removePayAccountBalance,
  selectCurrentSale,
  updatePayAccountBalanceAmount,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useSaleManager } from '../../../ng-compat/hooks'
import { useFormatCurrencyValue } from '../summary-panel/useFormatCurrencyValue'
import { EVENT } from '../../../constants'
import { WEB_REGISTER } from '../../web-register.constant'
import { getFeatureFromCache } from '../../../util/feature-cache'
import {
  FEATURE_ENABLE_ACH_XSERIES,
  FEATURE_ON_ACCOUNT_LAYBY_BALANCE_SPLIT,
} from '../../../constants/wr-features'
import { fetchPendingPaymentAmount } from '../../../functions/invoicing/fetch-pending-payment-amount'
import { getMaxPayableAmount } from '../../../functions/invoicing/max-payable-amount'

interface Props {
  close: () => void
}

type InTransitState =
  | { status: 'loading' }
  | { status: 'ok'; amount: number }
  | { status: 'error' }

export const AccountBalanceSetupModal: React.FunctionComponent<Props> = ({
  close,
}) => {
  const t = useT()
  const sale = useAppSelector(selectCurrentSale)
  const accountBalance = sale.payAccountBalance
  const saleCustomer = sale.customer
  const customerBalances = sale.customerBalances
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const formatCurrencyValue = useFormatCurrencyValue()

  // Customer account balance
  // `balance` is in negative so creating an absolute value from it
  const currentBalance = vn(customerBalances?.account).abs().toNumber()
  // Worst case scenorio
  const customer = saleCustomer?.name || saleCustomer?.email || 'This customer'
  const isLaybyBalanceSplitEnabled = getFeatureFromCache(
    FEATURE_ON_ACCOUNT_LAYBY_BALANCE_SPLIT
  )
  const isAchEnabled = getFeatureFromCache(FEATURE_ENABLE_ACH_XSERIES)
  // Initial selected amount
  const amount = accountBalance?.amount

  const [inTransit, setInTransit] = useState<InTransitState>(
    isAchEnabled && saleCustomer?.id
      ? { status: 'loading' }
      : { status: 'ok', amount: 0 }
  )
  // Holds the value of the input field
  // Default is the existing payment amount, or the full account balance
  const [selectedAmount, setSelectedAmount] = useState<number | string>(
    amount || currentBalance
  )
  const [valid, setValid] = useState<boolean>(true)
  const hasAppliedInTransitCap = useRef(false)

  useEffect(() => {
    hasAppliedInTransitCap.current = false
    setValid(true)

    if (!isAchEnabled || !saleCustomer?.id) {
      setInTransit({ status: 'ok', amount: 0 })
      return
    }

    setInTransit({ status: 'loading' })

    let isCurrent = true
    fetchPendingPaymentAmount(saleCustomer.id).then(result => {
      if (!isCurrent) {
        return
      }
      setInTransit(
        result.known
          ? { status: 'ok', amount: result.amount }
          : { status: 'error' }
      )
    })

    return () => {
      isCurrent = false
    }
  }, [isAchEnabled, saleCustomer?.id])

  const inTransitAmount = inTransit.status === 'ok' ? inTransit.amount : 0
  const maxPayable = getMaxPayableAmount(currentBalance, inTransitAmount)
  const showInTransit = inTransit.status === 'ok' && inTransitAmount > 0
  const isFullyCoveredByInTransit =
    inTransit.status === 'ok' && inTransitAmount > 0 && maxPayable === 0
  const isInTransitUnknown =
    inTransit.status === 'loading' || inTransit.status === 'error'

  useEffect(() => {
    if (inTransit.status === 'loading') {
      hasAppliedInTransitCap.current = false
      return
    }
    if (inTransit.status !== 'ok' || hasAppliedInTransitCap.current) {
      return
    }
    hasAppliedInTransitCap.current = true
    const payable = getMaxPayableAmount(currentBalance, inTransit.amount)
    setSelectedAmount(previous => {
      if (amount) {
        const current = vn(previous).toNumber()
        return Number.isFinite(current) ? Math.min(current, payable) : payable
      }
      return payable
    })
    if (payable > 0) {
      setValid(true)
    }
  }, [inTransit, currentBalance, amount])

  // Input field validation rules
  const validators: NumberInputValidators = {
    nonNegative: true,
    nonEmpty: true,
    nonZero: true,
    max: isInTransitUnknown ? currentBalance : maxPayable,
    decimalPlaces: 2,
  }
  const isSubmitDisabled = !valid || isInTransitUnknown || maxPayable === 0

  const persistAndClose = async (currentAction: string) => {
    await saleManager.saveSale()

    $rootScope.$emit(EVENT.SALE_UPDATE_TENDERED_AMOUNT)
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'Clicked: Account Balance Payment',
      { action: currentAction, saleId: sale.localId }
    )

    close()
  }

  const dismissModal = () => {
    close()
  }

  const submitPayment = async (selectedAmountToPay: number | string) => {
    if (isInTransitUnknown || maxPayable === 0) {
      return
    }
    const payableAmount = vn(selectedAmountToPay).toNumber()
    if (
      !Number.isFinite(payableAmount) ||
      payableAmount <= 0 ||
      payableAmount > maxPayable
    ) {
      return
    }
    dispatch(updatePayAccountBalanceAmount(selectedAmountToPay))
    await persistAndClose(amount ? 'Edit' : 'Add')
  }

  const removePayment = async () => {
    dispatch(removePayAccountBalance())
    await persistAndClose('Remove')
  }
  // Blur handler for the input field
  // This will convert the input value to 2 dp
  const handleBlur = (value: number | string) => {
    setSelectedAmount(vn(value).toFixed(2))
  }

  return ReactDOM.createPortal(
    <Modal onClose={dismissModal} className="vd-product-modal">
      <ModalHeader>Add an Account Balance payment.</ModalHeader>
      <ModalContent>
        <p>
          {isLaybyBalanceSplitEnabled ? (
            <>
              {customer} has unpaid on account sales totalling ${currentBalance}
              . The oldest sales will be paid off first.
            </>
          ) : (
            <>
              {customer} has unpaid On Account and Layby sales totalling $
              {currentBalance}. The oldest sales will be paid off first.
            </>
          )}
        </p>
        {inTransit.status === 'loading' && (
          <div role="status" aria-live="polite">
            <LoaderSpinner
              small
              aria-label={t('Checking in-transit payments', {
                _context:
                  'Account balance payment modal loading spinner while fetching ACH in-transit amount',
              })}
            />
          </div>
        )}
        {showInTransit && (
          <p>
            {t('In-transit: {amount}', {
              amount: formatCurrencyValue(inTransitAmount),
              _context:
                'Account balance payment modal showing ACH in-transit amount under the account balance',
            })}
          </p>
        )}
        {inTransit.status === 'error' && (
          <p>
            {t(
              'Unable to confirm in-transit payments. Add Payment is disabled.',
              {
                _context:
                  'Account balance payment modal fail-closed when in-transit amount cannot be fetched',
              }
            )}
          </p>
        )}
        {isFullyCoveredByInTransit && (
          <p>
            {t(
              'Nothing left to collect. An in-transit payment covers this balance.',
              {
                _context:
                  'Account balance payment modal when in-transit ACH covers the outstanding amount',
              }
            )}
          </p>
        )}
        {!isFullyCoveredByInTransit && (
          <GridRow>
            <GridColumn small="12/12" medium="5/12">
              <FieldLabel label="Amount to Pay">
                <CurrencyInput
                  onChange={() => {
                    setValid(true)
                  }}
                  onBlur={handleBlur}
                  value={selectedAmount}
                  placeholder="0.00"
                  validators={validators}
                  onError={() => {
                    setValid(false)
                  }}
                />
              </FieldLabel>
            </GridColumn>
          </GridRow>
        )}
      </ModalContent>
      <ModalActions>
        <div className="vd-btn-group">
          {/* Add mode */}
          {!amount && (
            <>
              <Button
                variant="supplementary"
                onClick={dismissModal}
                data-track="not-add-payment-account-balance-setup"
              >
                Don't Add Payment
              </Button>
              <Button
                variant="go"
                disabled={isSubmitDisabled}
                onClick={() => {
                  submitPayment(selectedAmount)
                }}
                data-cy="add-account-balance-payment-button"
                data-track="add-payment-account-balance-setup"
              >
                Add Payment
              </Button>
            </>
          )}
          {/* Edit/update mode */}
          {amount && (
            <>
              <Button
                variant="supplementary"
                onClick={() => {
                  removePayment()
                }}
                data-cy="remove-account-balance-payment-button"
                data-track="remove-payment-account-balance-setup"
              >
                Remove Payment
              </Button>
              <Button
                variant="supplementary"
                onClick={dismissModal}
                data-track="not-update-payment-account-balance-setup"
              >
                Don't Update Payment
              </Button>
              <Button
                variant="go"
                disabled={isSubmitDisabled}
                onClick={() => {
                  submitPayment(selectedAmount)
                }}
                data-cy="update-account-balance-payment-button"
                data-track="update-payment-account-balance-setup"
              >
                Update Payment
              </Button>
            </>
          )}
        </div>
      </ModalActions>
    </Modal>,
    document.body
  )
}

export const AccountBalanceSetupModalComponent = reactular(
  AccountBalanceSetupModal,
  ['close'],
  'reactWrapper'
)
